import React, { useState, useEffect } from 'react';

import Popup from '../../Popup';
import Img from '../../Img';
import CheckboxSA from '../../FormElementsStandalone/CheckboxSA';

import styles from './editFacilitiesPopup.scss';

const EditFacilitiesPopup = (props) => {
  const {
    closePopup,
    confirmPopup,
    clubLocations,
    getClubLocations,
    selectedFacilityIds,
  } = props;

  const [error, setError] = useState(null);
  const [selectedFacilities, setSelectedFacilities] = useState([]);

  useEffect(() => {
    if (!clubLocations?.length || !selectedFacilityIds?.size) {
      setSelectedFacilities([]);
      return;
    }

    const preselected = clubLocations
      .flatMap((location) => location.facilities || [])
      .filter((facility) => selectedFacilityIds.has(facility.id));

    setSelectedFacilities(preselected);
  }, [clubLocations, selectedFacilityIds]);

  useEffect(() => {
    if (selectedFacilities.length > 0 && error) {
      setError(null);
    }
  }, [selectedFacilities, error]);

  const [openLocationIds, setOpenLocationIds] = useState(new Set());

  const toggleLocation = (locationId) => {
    setOpenLocationIds((prev) => {
      const next = new Set(prev);
      next.has(locationId) ? next.delete(locationId) : next.add(locationId);
      return next;
    });
  };

  const isLocationOpen = (locationId) => openLocationIds.has(locationId);

  return (
    <Popup
      className={`popup-md ${styles.popup}`}
      onCloseClick={closePopup}
      config={{
        fullScreen: false,
        noPadding: true,
        topGradient: true,
        style: {
          'max-height': '530px',
        },
      }}
    >
      <div className="popup-section p24 pt0 pb0">
        <div className={`${styles.title}`}>
          <span>Change or Add Locations and Facilities</span>
          <div
            className={`${styles.refresh}`}
            onClick={getClubLocations}
          >
            <Img src="/img/icons/refresh.svg" alt="Refresh Icon" />&nbsp;Refresh
          </div>
        </div>
      </div>
      <div className="popup-section p24">
        <div className={`${styles.content}`}>
          {clubLocations.map((location) => (
            <div key={location.id} className={styles.locationBlock}>
              {/* Location header */}
              <div
                className={styles.locationHeader}
                onClick={() => toggleLocation(location.id)}
              >
                <div
                  className={styles.checkbox}
                  onClick={(e) => e.stopPropagation()}
                >
                  <CheckboxSA
                    checked={location.facilities.length > 0 && location.facilities.every(
                      (facility) => selectedFacilities.some((f) => f.id === facility.id),
                    )}
                    onChange={() => {
                      setError(null);
                      setSelectedFacilities((prev) => {
                        const locationFacilityIds = location.facilities.map((f) => f.id);
                        const hasAll = locationFacilityIds.every((id) => prev.some((f) => f.id === id));

                        if (hasAll) {
                          // remove all facilities from this location
                          return prev.filter((f) => !locationFacilityIds.includes(f.id));
                        }

                        // add missing facilities from this location
                        const toAdd = location.facilities.filter(
                          (facility) => !prev.some((f) => f.id === facility.id),
                        );

                        return [...prev, ...toAdd];
                      });
                    }}
                  />
                </div>
                <div className={styles.locationName}>
                  {location.name}
                  {location.locationTypeId === 1 && ' (Primary)'}
                </div>
                <div className={styles.toggleLocation}>
                  <i
                    className={`uts uts-${isLocationOpen(location.id)
                      ? 'collapse'
                      : 'expand'
                      }-arrow`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleLocation(location.id);
                    }}
                  />
                </div>
              </div>
              {/* Facilities */}
              {isLocationOpen(location.id) && (
                <div className={styles.facilitiesWrapper}>
                  {location.facilities.map((facility) => {
                    const isSelected = selectedFacilities.some(
                      (f) => f.id === facility.id,
                    );

                    return (
                      <div
                        key={facility.id}
                        className={styles.facilityRow}
                        onClick={() => {
                          setError(null);
                          setSelectedFacilities((prev) => (isSelected
                            ? prev.filter((f) => f.id !== facility.id)
                            : [...prev, facility]));
                        }}
                      >
                        <CheckboxSA
                          checked={isSelected}
                          readOnly
                        />
                        {facility.name}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
        {error && (
          <div className={`${styles.error}`}>{error}</div>
        )}
        <div className={`row mt24`}>
          <div className={`col-md-6`}>
            <button
              type="button"
              className={`btn btn-default btn-sm text-uppercase btn-block`}
              onClick={closePopup}
            >
              Cancel
            </button>
          </div>
          <div className={`col-md-6`}>
            <button
              type="button"
              className={`btn btn-primary btn-sm text-uppercase btn-block`}
              onClick={() => {
                if (selectedFacilities.length > 0) {
                  confirmPopup(selectedFacilities);
                } else {
                  setError('You need to select at least 1 event facility');
                }
              }}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </Popup>
  );
};

export default EditFacilitiesPopup;
